import React, { useCallback, useEffect } from 'react'
import {
  StyledCol,
  StyledColContainer,
  StyledContainer,
  StyledContinueButtonContainer,
  StyledHeader,
  StyledRowContainer,
} from './ValueOwnershipForm.styled'
import Input from 'components/Input/Input'
import { useAppDispatch } from 'store/hooks'
import { useSelector } from 'react-redux'
import { removeFormField, selectAddressFormData, setFormField } from 'slices/addressFormSlice'
import { useTranslation } from 'react-i18next'
import Button from 'components/Button/Button'
import MultiSelect from 'components/MultiSelect/MultiSelect'
import { ObjectType, OwnershipType } from 'types/ApiResponse'
import { StyledInputAdornment } from 'components/Modal/ProductFormModal/ProductFormModal.styled'
import { handleNumberInput } from 'components/Modal/ProductFormModal/helper'

const ValueOwnershipForm = (props: any) => {
  const { setCurrentSubTab } = props
  const dispatch = useAppDispatch()
  const addressFormData = useSelector(selectAddressFormData)
  const { t } = useTranslation()
  const objectType = addressFormData.addressData?.propertyDetails?.objectType

  const getOwnershipOptions = () => {
    const options = [
      {
        label: `ownership.${OwnershipType.FULL.toLowerCase()}`,
        value: OwnershipType.FULL,
      },
      {
        label: `ownership.${OwnershipType.LEASE.toLowerCase()}`,
        value: OwnershipType.LEASE,
      },
      {
        label: `ownership.${OwnershipType.SHARED.toLowerCase()}`,
        value: OwnershipType.SHARED,
      },
      {
        label: `ownership.${OwnershipType.SHARED_LEASE.toLowerCase()}`,
        value: OwnershipType.SHARED_LEASE,
      },
    ]

    switch (objectType) {
      case ObjectType.HOUSE:
        return options
      case ObjectType.APARTMENT:
        return options.filter(
          (item) => item.value === OwnershipType.SHARED || item.value === OwnershipType.SHARED_LEASE
        )
      case ObjectType.MULTI_FAMILY_HOUSE:
        return options.filter(
          (item) => item.value === OwnershipType.FULL || item.value === OwnershipType.LEASE
        )
    }
    return []
  }

  const getDefaultOwnership = useCallback(() => {
    switch (objectType) {
      case ObjectType.HOUSE:
        return OwnershipType.FULL
      case ObjectType.APARTMENT:
        return OwnershipType.SHARED
      case ObjectType.MULTI_FAMILY_HOUSE:
        return OwnershipType.FULL
    }
    return null
  }, [objectType])

  const validateNumericField = useCallback(
    (currentValue, min, max, key, isFloat = false) => {
      const numericValue = isFloat
        ? parseFloat(currentValue.replace(',', '.'))
        : parseInt(currentValue)
      const numericMin = parseInt(min)
      const numericMax = parseInt(max)
      if (numericValue >= numericMax) {
        dispatch(setFormField({ key, value: `${numericMax}` }))
      } else if (numericValue <= numericMin) {
        dispatch(setFormField({ key, value: `${numericMin}` }))
      }
    },
    [dispatch]
  )

  const validationMultiSelect = (value, options, formFieldName) => {
    if (!options) {
      return value
    }
    const matchingOption = options.find((option) => option.value === value || option.key === value)
    if ((value && !matchingOption) || !value) {
      dispatch(removeFormField(formFieldName))
      return false
    }
    return true
  }

  const onChangeGroundLeaseActualRent = useCallback(
    (groundLeaseActualRent: string, validate: boolean = false) => {
      if (groundLeaseActualRent && groundLeaseActualRent.length === 0) {
        dispatch(removeFormField('propertyDetails.groundLeaseActualRent'))
        return
      }
      const regex = /^\d*(,\d{0,2})?$/
      if (regex.test(groundLeaseActualRent)) {
        dispatch(
          setFormField({
            key: 'propertyDetails.groundLeaseActualRent',
            value: groundLeaseActualRent,
          })
        )
        if (validate) {
          validateNumericField(
            groundLeaseActualRent,
            0,
            1000000,
            'propertyDetails.groundLeaseActualRent',
            true
          )
        }
      }
    },
    [dispatch, validateNumericField]
  )

  const onChangeGroundLeaseRemainingTerm = useCallback(
    (groundLeaseRemainingTerm: string, validate: boolean = false) => {
      if (groundLeaseRemainingTerm && groundLeaseRemainingTerm.length === 0) {
        dispatch(removeFormField('propertyDetails.groundLeaseRemainingTerm'))
        return
      }
      dispatch(
        setFormField({
          key: 'propertyDetails.groundLeaseRemainingTerm',
          value: groundLeaseRemainingTerm,
        })
      )
      if (validate) {
        validateNumericField(
          groundLeaseRemainingTerm,
          16,
          1000000,
          'propertyDetails.groundLeaseRemainingTerm'
        )
      }
    },
    [dispatch, validateNumericField]
  )

  const onChangeOwnership = useCallback(
    (ownership: OwnershipType) => {
      dispatch(setFormField({ key: 'propertyDetails.ownership', value: ownership }))
      if (ownership === OwnershipType.FULL || ownership === OwnershipType.SHARED) {
        onChangeGroundLeaseActualRent('')
        onChangeGroundLeaseRemainingTerm('')
      }
      if (ownership === OwnershipType.FULL || ownership === OwnershipType.LEASE) {
        dispatch(removeFormField('propertyDetails.coownershipNumerator'))
        dispatch(removeFormField('propertyDetails.coownershipDenominator'))
      }
    },
    [dispatch, onChangeGroundLeaseActualRent, onChangeGroundLeaseRemainingTerm]
  )

  const onBackClick = () => {
    setCurrentSubTab((tab) => tab - 1)
  }

  const onContinueClick = () => {
    setCurrentSubTab((tab) => tab + 1)
  }

  useEffect(() => {
    if (!addressFormData.addressData?.propertyDetails?.ownership) {
      onChangeOwnership(getDefaultOwnership())
    }
  }, [objectType, addressFormData.addressData, onChangeOwnership, getDefaultOwnership])

  useEffect(() => {
    onChangeGroundLeaseActualRent(
      addressFormData.addressData?.propertyDetails?.groundLeaseActualRent,
      true
    )
    onChangeGroundLeaseRemainingTerm(
      addressFormData.addressData?.propertyDetails?.groundLeaseRemainingTerm,
      true
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <StyledContainer>
        <StyledHeader>{t('newAddressModal.objectDataTab.headers.ownership')}</StyledHeader>
        <StyledRowContainer>
          <StyledColContainer>
            <StyledCol>
              <MultiSelect
                value={addressFormData.addressData?.propertyDetails?.ownership || ''}
                options={getOwnershipOptions()}
                label={`${t('newAddressModal.objectDataTab.ownership')}`}
                onChange={(event) => onChangeOwnership(event.target.value)}
                validation={validationMultiSelect}
                formFieldName="propertyDetails.ownership"
              />
            </StyledCol>
          </StyledColContainer>
        </StyledRowContainer>
        <StyledHeader
          disabled={
            addressFormData.addressData?.propertyDetails?.ownership === OwnershipType.FULL ||
            addressFormData.addressData?.propertyDetails?.ownership === OwnershipType.SHARED
          }
        >
          {t('newAddressModal.objectDataTab.headers.ground_lease')}
        </StyledHeader>
        <StyledRowContainer>
          <StyledColContainer>
            <StyledCol>
              <Input
                disabled={
                  addressFormData.addressData?.propertyDetails?.ownership === OwnershipType.FULL ||
                  addressFormData.addressData?.propertyDetails?.ownership === OwnershipType.SHARED
                }
                value={addressFormData.addressData?.propertyDetails?.groundLeaseActualRent || ''}
                onChange={(event) => onChangeGroundLeaseActualRent(event.target.value)}
                onBlur={(event) => onChangeGroundLeaseActualRent(event.target.value, true)}
                onKeyPress={(event) => handleNumberInput(event, true)}
                defaultValue={
                  addressFormData.addressData?.propertyDetails?.groundLeaseActualRent || ''
                }
                label={`${t('newAddressModal.objectDataTab.groundLeaseActualRent')}`}
                InputProps={{
                  endAdornment: (
                    <StyledInputAdornment
                      position="end"
                      disabled={
                        addressFormData.addressData?.propertyDetails?.ownership ===
                          OwnershipType.FULL ||
                        addressFormData.addressData?.propertyDetails?.ownership ===
                          OwnershipType.SHARED
                      }
                    >
                      {t('newAddressModal.objectDataTab.groundLeaseActualRent.unit')}
                    </StyledInputAdornment>
                  ),
                }}
              />
            </StyledCol>
          </StyledColContainer>
          <StyledColContainer>
            <StyledCol>
              <Input
                disabled={
                  addressFormData.addressData?.propertyDetails?.ownership === OwnershipType.FULL ||
                  addressFormData.addressData?.propertyDetails?.ownership === OwnershipType.SHARED
                }
                value={addressFormData.addressData?.propertyDetails?.groundLeaseRemainingTerm || ''}
                onChange={(event) => onChangeGroundLeaseRemainingTerm(event.target.value)}
                onBlur={(event) => onChangeGroundLeaseRemainingTerm(event.target.value, true)}
                onKeyPress={(event) => handleNumberInput(event)}
                defaultValue={
                  addressFormData.addressData?.propertyDetails?.groundLeaseRemainingTerm || ''
                }
                label={`${t('newAddressModal.objectDataTab.groundLeaseRemainingTerm')}`}
                helperText={`min. 16`}
                InputProps={{
                  endAdornment: (
                    <StyledInputAdornment
                      position="end"
                      disabled={
                        addressFormData.addressData?.propertyDetails?.ownership ===
                          OwnershipType.FULL ||
                        addressFormData.addressData?.propertyDetails?.ownership ===
                          OwnershipType.SHARED
                      }
                    >
                      {t('newAddressModal.objectDataTab.groundLeaseRemainingTerm.unit')}
                    </StyledInputAdornment>
                  ),
                }}
              />
            </StyledCol>
          </StyledColContainer>
        </StyledRowContainer>
      </StyledContainer>
      <StyledContinueButtonContainer>
        <Button kind="primary" onClick={() => onBackClick()}>
          {t('common.backButton')}
        </Button>
        <Button kind="primary" onClick={() => onContinueClick()}>
          {t('common.continueButton')}
        </Button>
      </StyledContinueButtonContainer>
    </>
  )
}

export default ValueOwnershipForm
